<template>
  <header class="n3-cabecera" :class="{ 'n3-cabecera--transparente': transparente, 'n3-cabecera--abierta': abierto }" v-if="NOTARIA?.Theme">
    <div class="n2-contenedor n3-cabecera__fila">
      <NuxtLink :to="getLocalePath('/')" class="n3-logo" :aria-label="NOTARIA.Nombre">
        <img v-if="T.Menu?.Logo" :src="imagenIDtoSRC(T.Menu.Logo)" :alt="'Logo ' + NOTARIA.Nombre" />
        <span v-else>{{ NOTARIA.Nombre }}</span>
      </NuxtLink>

      <nav class="n3-nav" :aria-label="$t('Inicio')">
        <template v-for="e in enlaces" :key="e.clave">
          <a v-if="e.externo" :href="e.href" target="_blank" rel="noopener">{{ e.texto }}</a>
          <NuxtLink v-else :to="e.to">{{ e.texto }}</NuxtLink>
        </template>
      </nav>

      <div class="n3-cabecera__acciones">
        <button v-if="NOTARIA.Personalizar?.Accesibilidad" type="button" class="n3-icono" @click="store.toggleModoAccesible"
          :aria-label="$t(store.ModoAccesible ? 'DesactivarModoAccesibilidad' : 'ActivarModoAccesibilidad')">
          <v-icon>mdi-human</v-icon>
        </button>
        <v-menu v-if="NOTARIA.Personalizar?.Multilenguaje && idiomas.length > 1">
          <template v-slot:activator="{ props }">
            <button type="button" class="n3-icono" v-bind="props" :aria-label="$t('seleccionIdioma')">
              <IconoIdioma :flag="store.getFlag" />
            </button>
          </template>
          <v-list density="compact">
            <v-list-item v-for="i in idiomas" :key="i.Language" @click="cambiarIdioma(i)" :title="i.Title">
              <template v-slot:prepend><IconoIdioma :flag="i.Flag" class="mr-2" /></template>
            </v-list-item>
          </v-list>
        </v-menu>
        <NuxtLink v-if="acceso.visible('menu')" :to="acceso.enlace('menu')" class="n2-boton n2-boton--acento n3-solo-escritorio">
          {{ tSolicitud('Encargo online') }}
        </NuxtLink>
        <a v-else-if="C.Telefono" :href="'tel:' + C.Telefono.replace(/\s/g, '')" class="n2-boton n2-boton--acento n3-solo-escritorio">
          <v-icon size="18">mdi-phone-outline</v-icon> {{ C.Telefono.trim() }}
        </a>
        <button type="button" class="n3-hamburguesa" :aria-expanded="abierto" aria-controls="n3-menu" aria-label="Menú" @click="abierto = !abierto">
          <span></span><span></span>
        </button>
      </div>
    </div>

    <!-- Menú a pantalla completa (móvil, y en escritorio desde el botón) -->
    <div v-show="abierto" id="n3-menu" class="n3-menu" @click.self="abierto = false">
      <div class="n2-contenedor n3-menu__rejilla">
        <nav class="n3-menu__enlaces">
          <template v-for="(e, i) in enlaces" :key="'m' + e.clave">
            <a v-if="e.externo" :href="e.href" target="_blank" rel="noopener" :style="{ '--i': i }">{{ e.texto }}</a>
            <NuxtLink v-else :to="e.to" :style="{ '--i': i }" @click="abierto = false">{{ e.texto }}</NuxtLink>
          </template>
        </nav>
        <div class="n3-menu__contacto">
          <p v-if="C.Direccion">{{ C.Direccion }}<br>{{ [C.CP, C.Municipio].filter(Boolean).join(' ') }}</p>
          <a v-if="C.Telefono" :href="'tel:' + C.Telefono.replace(/\s/g, '')">{{ C.Telefono.trim() }}</a>
          <a v-if="C.Email" :href="'mailto:' + C.Email">{{ C.Email }}</a>
          <NuxtLink v-if="acceso.visible('menu')" :to="acceso.enlace('menu')" class="n2-boton n2-boton--acento" @click="abierto = false">
            {{ tSolicitud('Encargo online') }}
          </NuxtLink>
        </div>
      </div>
    </div>
  </header>
</template>

<script setup>
import { ref, computed, watch, onMounted, onBeforeUnmount } from 'vue'
import { storeToRefs } from 'pinia'
import { useStore } from '../../store/index'
import { useUtils } from '#imports'
import { useAccesoSolicitud } from '../../composables/solicitudAcceso'
import { t as tSolicitud } from '../../composables/solicitudTextos'
import { useMenuNotaria } from '../../composables/useNotaria2'

const props = defineProps({ sobrePortada: { type: Boolean, default: false } })

const store = useStore()
const { NOTARIA } = storeToRefs(store)
const { imagenIDtoSRC, $t, getLocalePath } = useUtils()
const acceso = useAccesoSolicitud()
const route = useRoute()
const { enlaces, idiomas, cambiarIdioma } = useMenuNotaria()

const T = computed(() => NOTARIA.value?.Theme ?? {})
const C = computed(() => NOTARIA.value?.Contacto ?? {})
const abierto = ref(false)
const conScroll = ref(false)
// Sobre la foto de portada la cabecera es transparente hasta que se hace scroll
const transparente = computed(() => props.sobrePortada && !conScroll.value && !abierto.value)

watch(() => route.fullPath, () => { abierto.value = false })
watch(abierto, (v) => { if (typeof document !== 'undefined') document.documentElement.style.overflow = v ? 'hidden' : '' })

function alHacerScroll() { conScroll.value = window.scrollY > 40 }
onMounted(() => { alHacerScroll(); window.addEventListener('scroll', alHacerScroll, { passive: true }) })
onBeforeUnmount(() => {
  window.removeEventListener('scroll', alHacerScroll)
  document.documentElement.style.overflow = ''
})
</script>
